import { fetchVideoSegmentPage } from '@/features/video/api/segments';
import { VIDEO_QUERY_KEY } from '@/features/video/config/constants';
import type { VideoSegment } from '@/features/video/types/contracts';
import type { ApiCallOptions } from '@/shared/api/client';
import { queryOptions, useQuery } from '@tanstack/react-query';

async function fetchAllVideoSegments(
  assetId: string,
  options: ApiCallOptions = {},
): Promise<VideoSegment[]> {
  const segments: VideoSegment[] = [];
  const seenCursors = new Set<string>();
  let cursor: string | undefined;

  do {
    const page = await fetchVideoSegmentPage(assetId, { cursor }, options);
    segments.push(...page.items);
    cursor = page.nextCursor ?? undefined;
    if (cursor) {
      if (seenCursors.has(cursor)) throw new Error('repeated_video_segment_cursor');
      seenCursors.add(cursor);
    }
  } while (cursor);

  return segments;
}

/**
 * An asset's clips. Asset-keyed and readable by anyone who can read the asset, so this
 * is independent of playback readiness — the timeline and the inspector's Clips section
 * render even when the stream itself is unavailable.
 */
export function videoSegmentsQuery(assetId: string) {
  return queryOptions({
    queryKey: [VIDEO_QUERY_KEY, 'segments', assetId],
    queryFn: ({ signal }) => fetchAllVideoSegments(assetId, { signal }),
    enabled: !!assetId,
  });
}

export function useVideoSegments(assetId: string) {
  return useQuery(videoSegmentsQuery(assetId));
}
